import React, { useEffect, useMemo, useRef, useState } from 'react';

import { SuperAdminAPI } from '../lib/api';

/**
 * Server-side searchable Organisation dropdown for the super-admin
 * "Create User" form.
 *
 * Why a custom component instead of a static <select> + loader
 * --------------------------------------------------------------
 * Taskydo organisations are tenants — there can easily be tens of
 * thousands once the platform scales.  Loading them all up-front
 * (the legacy ``loader`` pattern in ``ResourceForm``) would fetch
 * every row on page open and force the browser to do client-side
 * filtering — ruled out by the multi-tenant brief.
 *
 * Behaviour
 * ---------
 * * As the user types, debounce input (300 ms) and call
 *   ``GET /api/super_admin/organisations/lookup/?search=<q>`` —
 *   server returns at most 10 matches per request, paginated
 *   server-side.
 * * In-flight requests are aborted when the user keeps typing, so
 *   the latest keystroke always wins (no stale-result flicker).
 * * Each option shows ``Org name`` + ``sub_domain`` as a hint, and
 *   on selection the option's ``id`` is stored on the form.
 * * Loading, "no results", and error states are rendered inline —
 *   no global ⚠ toast, no React crash.
 *
 * Props
 * -----
 *  - ``value``        : current selected organisation id (number | string | '').
 *  - ``onChange(id)`` : called with the picked org id (or '' when cleared).
 *  - ``onSelectFull(org)`` : optional, also receives the full row
 *    ``{ id, name, sub_domain, schema_name }`` for callers that want
 *    to display / submit the schema_name.
 *  - ``required``, ``disabled``, ``placeholder`` : standard input UX.
 *  - ``id``           : id attribute (matches the parent <label htmlFor>).
 *  - ``initialOption``: optional pre-selected option to display before
 *    the user starts searching (used in edit-mode if we ever want to
 *    show the existing org).
 */
export default function OrganisationAutocomplete({
  id,
  value,
  onChange,
  onSelectFull,
  required = false,
  disabled = false,
  placeholder = 'Type to search organisations…',
  initialOption = null,
}) {
  // The user-visible search text in the input box.
  const [query, setQuery] = useState(initialOption ? formatOption(initialOption) : '');
  // Server results for the current search.
  const [options, setOptions] = useState([]);
  // The full org object (id, name, sub_domain, schema_name) that is
  // currently selected.  Kept locally so the input box can show the
  // friendly label even after the dropdown closes.
  const [selected, setSelected] = useState(initialOption);
  // UX flags.
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);

  // Refs we need for click-outside and abort-on-retype.
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // ------------------------------------------------------------------
  // Sync external ``value`` -> internal ``selected`` when it changes
  // out-of-band (e.g. a parent form reset).
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!value && selected) {
      setSelected(null);
      setQuery('');
    }
    // We deliberately do NOT fetch the org by id when ``value`` arrives
    // without a matching ``selected`` — the create-user form always
    // starts blank, and the parent already passes ``initialOption``
    // when an edit-mode flow needs a preselected row.
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // Click-outside closes the dropdown without losing the typed text.
  // ------------------------------------------------------------------
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ------------------------------------------------------------------
  // Debounced server-side search.
  //
  // Fires 300 ms after the user stops typing.  If they type again
  // before that window elapses, the previous request is aborted via
  // ``AbortController`` so the LATEST keystroke wins.
  // ------------------------------------------------------------------
  const triggerSearch = useMemo(
    () => (text) => {
      // Abort any pending fetch.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        setErrorMsg('');
        try {
          const data = await SuperAdminAPI.organisationsLookup({
            search: text,
            signal: ctrl.signal,
          });
          // DRF page shape: { count, next, previous, results }.
          // Some configs return a bare list — tolerate both.
          const rows = Array.isArray(data) ? data : (data && data.results) || [];
          setOptions(rows);
          setActiveIdx(rows.length ? 0 : -1);
        } catch (err) {
          // Aborted by the next keystroke — silently ignore so the
          // newer in-flight request can finish and render.
          if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) {
            return;
          }
          setOptions([]);
          setErrorMsg(extractError(err));
        } finally {
          // Only clear the flag if THIS request is still the in-flight one.
          if (abortRef.current === ctrl) {
            abortRef.current = null;
            setLoading(false);
          }
        }
      }, 300);
    },
    [],
  );

  // ------------------------------------------------------------------
  // Input change handler — clears any current selection (since the
  // user is typing fresh text), then schedules a search.
  // ------------------------------------------------------------------
  const onInput = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    if (selected) {
      setSelected(null);
      onChange('');
      if (onSelectFull) onSelectFull(null);
    }
    triggerSearch(text);
  };

  // ------------------------------------------------------------------
  // When the input gains focus, kick off an empty-query search so the
  // user immediately sees the first 10 organisations without having to
  // type.  Skip if we already have results cached for the same query.
  // ------------------------------------------------------------------
  const onFocus = () => {
    setOpen(true);
    if (options.length === 0 && !loading && !errorMsg) {
      triggerSearch(query.trim());
    }
  };

  const pick = (org) => {
    setSelected(org);
    setQuery(formatOption(org));
    setOpen(false);
    setOptions([]);
    onChange(org.id);
    if (onSelectFull) onSelectFull(org);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      triggerSearch(query.trim());
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && options[activeIdx]) {
        e.preventDefault();
        pick(options[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // ------------------------------------------------------------------
  // Render.  We keep markup minimal and lean on the existing `.ty-*`
  // classes from styles/global.css for visual consistency with the
  // rest of the form.  All inline styles here are layout-only (the
  // CSS file owns colour / typography).
  // ------------------------------------------------------------------
  return (
    <div ref={wrapperRef} className="ty-autocomplete" style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        className="ty-input"
        value={query}
        onChange={onInput}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        // We mark the *visible* input ``required`` only while no org
        // is yet selected, then drop the attribute once the user has
        // picked one.  This makes the browser's native "fill out this
        // field" tooltip point at the actual textbox the user
        // interacts with — and avoids the Chromium "invalid form
        // control is not focusable" warning that fires when a hidden
        // <input required> is used.
        //
        // The backend (``UserViewSet.create``) is the source of truth
        // and re-validates ``organisation`` regardless of what the
        // browser does.
        required={required && !value}
        aria-required={required ? 'true' : undefined}
        aria-autocomplete="list"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`${id}-listbox`}
      />


      {open && (
        <div
          id={`${id}-listbox`}
          role="listbox"
          className="ty-autocomplete__menu"
          style={{
            position: 'absolute',
            zIndex: 50,
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div className="ty-autocomplete__hint" style={hintStyle}>
              Searching…
            </div>
          )}

          {!loading && errorMsg && (
            <div
              className="ty-autocomplete__hint"
              role="alert"
              style={{ ...hintStyle, color: '#b91c1c' }}
            >
              {errorMsg}
            </div>
          )}

          {!loading && !errorMsg && options.length === 0 && (
            <div className="ty-autocomplete__hint" style={hintStyle}>
              No organisations found.
            </div>
          )}

          {!loading && !errorMsg && options.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 4 }}>
              {options.map((org, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <li
                    key={org.id}
                    role="option"
                    aria-selected={isActive ? 'true' : 'false'}
                    onMouseDown={(e) => {
                      // ``mousedown`` (not ``click``) so the input
                      // doesn't lose focus before we register the pick.
                      e.preventDefault();
                      pick(org);
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: isActive ? 'var(--surface-2, #f3f4f6)' : 'transparent',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{org.name || '(unnamed)'}</div>
                    <div className="ty-muted" style={{ fontSize: 12 }}>
                      {org.sub_domain ? `${org.sub_domain}` : '—'}
                      {org.schema_name && org.schema_name !== org.sub_domain
                        ? ` · ${org.schema_name}`
                        : ''}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatOption(org) {
  if (!org) return '';
  const name = org.name || '(unnamed)';
  const sub = org.sub_domain ? ` (${org.sub_domain})` : '';
  return `${name}${sub}`;
}

/**
 * Pull a human-friendly message out of an axios error, falling back
 * to a generic "Couldn't load…" string so the dropdown never crashes
 * with [object Object].
 */
function extractError(err) {
  if (!err) return "Couldn't load organisations.";
  const data = err.response && err.response.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    if (data.detail) return String(data.detail);
    // Pick the first non-empty value if it's a list / dict.
    const first = Object.values(data).find(Boolean);
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return err.message || "Couldn't load organisations.";
}

const hintStyle = {
  padding: '10px 12px',
  fontSize: 13,
  color: 'var(--muted, #6b7280)',
};
